import { useScrollReveal, useCountUp } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';
import { achievementStats } from '@/mocks/homeData';
import TiltCard from '@/components/feature/TiltCard';

function StatCard({ stat, delay, forceVisible }: { stat: typeof achievementStats[0]; delay: number; forceVisible?: boolean }) {
  const { ref, value, pulse } = useCountUp(stat.value, 1800, 0, stat.value % 1 !== 0 ? 1 : 0, forceVisible);
  const { ref: cardRef, isVisible } = useScrollReveal(0.2, forceVisible);

  return (
    <div
      ref={cardRef}
      className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <TiltCard
        className={`relative p-6 md:p-7 rounded-2xl group ${
          stat.highlight
            ? 'card-premium-accent'
            : 'card-premium'
        }`}
      >
        {/* 数字区域 */}
        <div ref={ref} className="mb-3">
          <div className={`number-font text-4xl md:text-5xl lg:text-[3.5rem] text-primary-700 font-bold tracking-tight leading-none flex items-baseline gap-1 ${pulse ? 'counter-pop' : ''}`}>
            {value.toLocaleString()}
            <span className="text-2xl md:text-3xl text-gold-500">{stat.suffix}</span>
          </div>
        </div>

        {/* 标题与描述 */}
        <div className="font-body text-sm font-semibold text-foreground-800 mb-1">{stat.label}</div>
        <div className="text-xs text-foreground-500 font-body leading-relaxed">{stat.sub}</div>

        {/* 高亮标记 */}
        {stat.highlight && (
          <div className="absolute top-4 right-4">
            <div className="w-2 h-2 rounded-full bg-gold-400 animate-pulse-slow" />
          </div>
        )}

        {/* 底部装饰线 */}
        <div className="mt-4 pt-3 border-t border-background-200/50">
          <div className="flex items-center gap-1.5">
            <div className="w-8 h-0.5 rounded-full bg-primary-300/50" />
            <div className="w-4 h-0.5 rounded-full bg-accent-300/40" />
            <div className="w-2 h-0.5 rounded-full bg-primary-200/30" />
          </div>
        </div>
      </TiltCard>
    </div>
  );
}

export default function Section2Achievement({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.06);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.05);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-32 right-20 w-72 h-72 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-24 left-16 w-80 h-80 rounded-full bg-accent-100/8 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16">
        <div ref={titleRef} className="text-center mb-8 md:mb-10">
          <span
            className={`inline-block text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            成果基础
          </span>
          <h2
            className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-6 transition-all duration-1000 delay-100 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            六年数据积累与<span className="text-primary-600">系统验证</span>
          </h2>
          <p
            className={`text-base md:text-lg text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${
              titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            起点并不是为了追赶技术潮流，而是传统人工评价走到一定规模后，客观上遇到了效率和一致性的瓶颈。先有人工评价的专业标准，再有大规模数据积累，再有模型训练和人机验证，最后才形成可以推广的AI评课系统。
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-8">
          {achievementStats.map((stat, i) => (
            <StatCard key={stat.label} stat={stat} delay={i * 150} forceVisible={forceVisible} />
          ))}
        </div>

        <div
          className={`transition-all duration-1000 delay-700 ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="p-6 md:p-8 rounded-2xl card-premium">
            <div className="text-center mb-4">
              <span className="text-sm font-body text-gold-600 font-semibold uppercase tracking-wider">发展逻辑</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
              <span className="text-sm text-foreground-700 font-body px-4 py-2 rounded-xl glass-blue border-primary-200/30 font-medium">人工评价标准</span>
              <div className="flex items-center gap-1">
                <i className="ri-arrow-right-line text-gold-500 text-lg" />
              </div>
              <span className="text-sm text-foreground-700 font-body px-4 py-2 rounded-xl glass border-white/60 font-medium">数据积累</span>
              <div className="flex items-center gap-1">
                <i className="ri-arrow-right-line text-gold-500 text-lg" />
              </div>
              <span className="text-sm text-foreground-700 font-body px-4 py-2 rounded-xl glass border-white/60 font-medium">模型训练</span>
              <div className="flex items-center gap-1">
                <i className="ri-arrow-right-line text-gold-500 text-lg" />
              </div>
              <span className="text-sm font-body font-bold text-white px-5 py-2.5 rounded-xl gold-shimmer border-accent-200/40 shadow-sm">AI评课系统</span>
            </div>
            <p className="text-center text-xs text-foreground-400 font-body mt-4">* 最终数据以正式发布稿为准</p>
          </div>
        </div>
      </div>
    </div>
  );
}